import { safeReadProjlab, PROJLAB_PREFIX } from "./projlabStorage";
import { SLOTS, resolveStintIdentity, projectTeamPlayers } from "./projlabProjection";
import { STARTERS_2025 } from "../data/projectionData";

// Headless quick-edit lever engine — reads a team's saved projection, applies
// a small batch of slider-style edits to ONE slot/stint, and returns a fully
// recomputed { players, teamTotal } via the same single-sourced
// projectTeamPlayers used everywhere else. No writes: the caller owns
// persisting `updatedSave` back to localStorage (or not).
//
// Deliberately never touches mock1_player_edits_2026 (the per-player rate-
// edit store TeamEditor.jsx reads/writes) - quick-edit lives entirely inside
// one save's allocSummary/effSummary, same as the wizard itself.

// Levers available per position, for whatever UI eventually surfaces this.
// QUICK_LEVERS itself is just a catalog - applyQuickEdit classifies each
// field it's handed by name alone (see ALLOC_FIELDS/STINT_FIELDS below), so
// this list isn't consulted internally and doesn't gate what a caller sends.
const WR_TE_LEVERS = [
  { key: "tgtPct",   kind: "alloc" },
  { key: "games",    kind: "stint" },
  { key: "catchPct", kind: "rate" },
  { key: "ypr",      kind: "rate" },
  { key: "recTDPct", kind: "rate" },
];

export const QUICK_LEVERS = {
  QB: [
    { key: "rushPct",    kind: "alloc" },
    { key: "games",      kind: "stint" },
    { key: "intPct",     kind: "rate" },
    { key: "rushYPC",    kind: "rate" },
    { key: "rushTDPct",  kind: "rate" },
  ],
  RB: [
    { key: "rushPct",   kind: "alloc" },
    { key: "tgtPct",    kind: "alloc" },
    { key: "games",     kind: "stint" },
    { key: "ypc",       kind: "rate" },
    { key: "rushTDPct", kind: "rate" },
    { key: "catchPct",  kind: "rate" },
    { key: "ypr",       kind: "rate" },
    { key: "recTDPct",  kind: "rate" },
  ],
  WR: WR_TE_LEVERS,
  TE: WR_TE_LEVERS,
};

const ALLOC_FIELDS = new Set(["rushPct", "tgtPct"]);
const STINT_FIELDS = new Set(["games"]);

// Reads the currently persisted value of every lever for a position, using
// the same stintId/baseSlotKey derivation as applyQuickEdit - lets a UI seed
// its inputs from disk without duplicating the classification logic.
export function readLeverValues({ abbrev, slot, pos }) {
  const save = safeReadProjlab(abbrev);
  if (!save) return {};

  const stintId = slot.includes("#") ? slot : `${slot}#0`;
  const baseSlotKey = stintId.split("#")[0];

  const allocs = save.allocSummary?.allocs ?? {};
  const rates  = save.effSummary?.rates ?? {};
  const stint  = (allocs[baseSlotKey]?.stints || []).find(s => s.id === stintId);
  // Saves predating the stint model key rates by the bare slot (no `stints`
  // array at all) - mirror projectTeamPlayers' `rates[st.id] ?? rates[s.key]`
  // fallback so a legacy save's real rates are read, not a nonexistent
  // stint-keyed object.
  const rateKey = rates[stintId] ? stintId : (rates[baseSlotKey] ? baseSlotKey : stintId);

  const values = {};
  for (const lever of QUICK_LEVERS[pos] || []) {
    const { key } = lever;
    if (ALLOC_FIELDS.has(key)) values[key] = allocs[baseSlotKey]?.[key] ?? 0;
    else if (STINT_FIELDS.has(key)) values[key] = stint?.[key] ?? 17;
    else values[key] = rates[rateKey]?.[key] ?? 0;
  }
  return values;
}

export function applyQuickEdit({ abbrev, slot, changes, scoring }) {
  const save = safeReadProjlab(abbrev);
  if (!save) return null;

  const stintId = slot.includes("#") ? slot : `${slot}#0`;
  const baseSlotKey = stintId.split("#")[0];

  const allocs = JSON.parse(JSON.stringify(save.allocSummary?.allocs ?? {}));
  const rates  = JSON.parse(JSON.stringify(save.effSummary?.rates ?? {}));

  const teamStarters = STARTERS_2025[abbrev];
  const identitySt = (allocs[baseSlotKey]?.stints || []).find(s => s.id === stintId) || { id: stintId };
  const playerName = resolveStintIdentity(identitySt, baseSlotKey, teamStarters).player;
  // See readLeverValues - legacy saves store rates under the bare slot key
  // with no `stints` array. Target whichever key already holds the real
  // rates so a lever edit merges into it instead of shadowing it with a
  // new stint-keyed object that only has the one edited field.
  const rateKey = rates[stintId] ? stintId : (rates[baseSlotKey] ? baseSlotKey : stintId);

  const now = Date.now();
  const entries = [];

  for (const [field, newValue] of Object.entries(changes || {})) {
    let oldValue;
    if (ALLOC_FIELDS.has(field)) {
      if (!allocs[baseSlotKey]) allocs[baseSlotKey] = {};
      oldValue = allocs[baseSlotKey][field] ?? 0;
      allocs[baseSlotKey][field] = newValue;
    } else if (STINT_FIELDS.has(field)) {
      if (!allocs[baseSlotKey]) allocs[baseSlotKey] = {};
      if (!allocs[baseSlotKey].stints) allocs[baseSlotKey].stints = [{ id: stintId, games: 17 }];
      let target = allocs[baseSlotKey].stints.find(s => s.id === stintId);
      if (!target) { target = { id: stintId, games: 17 }; allocs[baseSlotKey].stints.push(target); }
      oldValue = target[field] ?? 17;
      target[field] = newValue;
    } else {
      if (!rates[rateKey]) rates[rateKey] = {};
      oldValue = rates[rateKey][field] ?? 0;
      rates[rateKey][field] = newValue;
    }
    entries.push({ ts: now, playerOrStep: playerName, field, oldValue, newValue, note: "quick-edit" });
  }

  const totalRush = SLOTS.reduce((s, sl) => s + (allocs[sl.key]?.rushPct ?? 0), 0);
  const totalTgt  = SLOTS.reduce((s, sl) => s + (allocs[sl.key]?.tgtPct  ?? 0), 0);

  const { players, teamTotal } = projectTeamPlayers({
    abbrev, totalPlays: save.totalPlays, passPct: save.passPct, allocs, rates, teamStarters, scoring,
  });

  return {
    updatedSave: { ...save, allocSummary: { allocs, totalRush, totalTgt }, effSummary: { teamTotal, players, rates } },
    entries,
  };
}

// Persists an applyQuickEdit() result - same localStorage write pattern as
// TeamEditor's own save effect. Appends `entries` to the changelog; never
// bumps version/schemaVersion fields (a quick-edit isn't a schema change).
export function commitQuickEdit(abbrev, updatedSave, entries) {
  const withChangelog = { ...updatedSave, changelog: [...(updatedSave.changelog || []), ...(entries || [])] };
  try { localStorage.setItem(`${PROJLAB_PREFIX}${abbrev}`, JSON.stringify(withChangelog)); } catch {}
  return withChangelog;
}
